// Updated: 20251207070259
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import WhatsAppButton from './components/WhatsAppButton';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import WelcomePage from './pages/WelcomePage';
import DashboardPage from './pages/DashboardPage';
import DashboardRouter from './pages/DashboardRouter';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import DatabaseManagementPage from './pages/DatabaseManagementPage';
import SetupPage from './pages/SetupPage';
import RoutersPage from './pages/RoutersPage';
import RouterSetupWizard from './pages/RouterSetupWizard';
import RouterStep1Script from './pages/RouterStep1Script';
import RouterStep2Verify from './pages/RouterStep2Verify';
import RouterStep3Connect from './pages/RouterStep3Connect';
import RouterDiagnosticsPage from './pages/RouterDiagnosticsPage';

import ReconnectStep1 from './pages/ReconnectStep1';
import ReconnectStep2 from './pages/ReconnectStep2';


import PlansPage from './pages/PlansPage';
import PointsOfSalePage from './pages/PointsOfSalePage';
import VouchersPage from './pages/VouchersPage';
import VouchersAdminPage from './pages/VouchersAdminPage';
import SellersPage from './pages/SellersPage';
import SellerSalesPage from './pages/SellerSalesPage';
import TicketDesignerPage from './pages/TicketDesignerPage';
import TicketDesignerMainPage from './pages/TicketDesignerMainPage';
import TicketDesignerSimple from './pages/TicketDesignerSimple';
import PaymentReportsPage from './pages/PaymentReportsPage';
import AdminDepositsPage from './pages/AdminDepositsPage';
import ProfilePage from './pages/ProfilePage';
import CortesPage from './pages/CortesPage';
import ReportesPage from './pages/ReportesPage';
import VPNInfoPage from './pages/VPNInfoPage';
import PortalDesignerPage from './pages/PortalDesignerPage';
import PortalDesignerEditor from './pages/PortalDesignerEditor';
import ScriptsGeneratorPage from './pages/ScriptsGeneratorPage';
import BalanceadorWizard from './pages/BalanceadorWizard';
import HotspotWizard from './pages/HotspotWizard';
import FailoverWizard from './pages/FailoverWizard';
import ScriptsHistory from './pages/ScriptsHistory';
import MaintenancePage from './pages/MaintenancePage';
import LicensePricingPage from './pages/LicensePricingPage';
import PlanSelectionPage from './pages/PlanSelectionPage';
import PPPoEPlansPage from './pages/PPPoEPlansPage';
import PPPoEClientsPage from './pages/PPPoEClientsPage';
import NewPPPoEClientPage from './pages/NewPPPoEClientPage';
import PPPoEClientDetailPage from './pages/PPPoEClientDetailPage';
import PPPoEReportsPage from './pages/PPPoEReportsPage';
import ReportPaymentPage from './pages/ReportPaymentPage';
import PaymentReportsAdminPage from './pages/PaymentReportsAdminPage';
import ToolsPaymentReportsPage from './pages/ToolsPaymentReportsPage';
import PlanSelectionPageTable from './pages/PlanSelectionPageTable';
import WhatsAppNotificationsPage from './pages/WhatsAppNotificationsPage';
import CronConfigPage from './pages/CronConfigPage';
import ExpensesPage from './pages/ExpensesPage';
import VoucherMaintenancePage from './pages/VoucherMaintenancePage';
import UpgradePlanPage from './pages/UpgradePlanPage';
import ToolsPurchasePage from './pages/ToolsPurchasePage';
import PaymentSummaryPage from './pages/PaymentSummaryPage';
import PreciosPlanesPage from './pages/PreciosPlanesPage';
import TutorialesPage from './pages/TutorialesPage';
import VideoTutorialsManagementPage from './pages/VideoTutorialsManagementPage';
import PaymentRequestsManagement from './pages/PaymentRequestsManagement';
import PruebaUserV7 from './pages/PruebaUserV7';
import ScriptsDelSistema from './pages/ScriptsDelSistema';

const ProtectedRoute = ({ children, skipLicenseCheck = false }) => {
  const { user, loading, license } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  // Not logged in? Go to login
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Super admin can access everything without license
  if (user.role === 'super_admin') {
    return children;
  }
  
  // Skip license check for specific pages (setup, welcome, router-setup, upgrade-plan)
  if (skipLicenseCheck) {
    return children;
  }
  
  // Sistema modular: Ya no se usa /welcome para inicializar VPN
  // VPN se inicializa on-demand al agregar routers
  // if (user.role === 'admin' && !localStorage.getItem('vpn_initialized') && !license?.vpn_initialized) {
  //   return <Navigate to="/welcome" replace />;
  // }
  
  // License check removed - user always has license after registration
  
  // All good, allow access
  return children;
};

// Ya no es necesaria - usar ProtectedRoute con skipLicenseCheck

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  // NOT logged in? Show the public page (login/register)
  if (!user) {
    return children;
  }
  
  // User IS logged in, redirect based on their status
  
  // Super admin always goes to dashboard (no license needed)
  if (user.role === 'super_admin') {
    return <Navigate to="/dashboard" />;
  }
  
  // Seller always goes to seller-sales
  if (user.role === 'seller') {
    return <Navigate to="/seller-sales" />;
  }
  
  // En el sistema modular, todos los usuarios siempre tienen licencia después del registro
  // Ya no es necesario ir a /welcome
  // if (!user.license_id) {
  //   return <Navigate to="/welcome" />;
  // }
  
  // User has license, go to dashboard
  return <Navigate to="/dashboard" />;
};

function AppContent() {
  return (
    <Router>
      {/* Botón flotante de WhatsApp (visible en todas las páginas) */}
      <WhatsAppButton />
      
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } />
        <Route path="/tutoriales" element={<TutorialesPage />} />
        <Route path="/precio-planes" element={<PreciosPlanesPage />} />
        <Route path="/welcome" element={
          <ProtectedRoute skipLicenseCheck={true}>
            <WelcomePage />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        } />
        <Route path="/super-admin" element={
          <ProtectedRoute>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/cron-config" element={
          <ProtectedRoute>
            <CronConfigPage />
          </ProtectedRoute>
        } />
        <Route path="/database-management" element={
          <ProtectedRoute>
            <DatabaseManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/setup" element={
          <ProtectedRoute skipLicenseCheck={true}>
            <SetupPage />
          </ProtectedRoute>
        } />
        <Route path="/routers" element={
          <ProtectedRoute>
            <RoutersPage />
          </ProtectedRoute>
        } />
        <Route path="/router-setup" element={
          <ProtectedRoute skipLicenseCheck={true}>
            <RouterSetupWizard />
          </ProtectedRoute>
        } />
        <Route path="/router-step1-script" element={
          <ProtectedRoute skipLicenseCheck={true}>
            <RouterStep1Script />
          </ProtectedRoute>
        } />
        <Route path="/router-step2-verify" element={
          <ProtectedRoute skipLicenseCheck={true}>
            <RouterStep2Verify />
          </ProtectedRoute>
        } />
        <Route path="/router-step3-connect" element={
          <ProtectedRoute skipLicenseCheck={true}>
            <RouterStep3Connect />
          </ProtectedRoute>
        } />
        <Route path="/router-diagnostics/:id" element={
          <ProtectedRoute>
            <RouterDiagnosticsPage />
          </ProtectedRoute>
        } />
        <Route path="/reconectar-step1" element={
          <ProtectedRoute skipLicenseCheck={true}>
            <ReconnectStep1 />
          </ProtectedRoute>
        } />
        <Route path="/reconectar-step2" element={
          <ProtectedRoute skipLicenseCheck={true}>
            <ReconnectStep2 />
          </ProtectedRoute>
        } />


        <Route path="/plans" element={
          <ProtectedRoute>
            <PlansPage />
          </ProtectedRoute>
        } />
        <Route path="/points-of-sale" element={
          <ProtectedRoute>
            <PointsOfSalePage />
          </ProtectedRoute>
        } />
        <Route path="/vouchers" element={
          <ProtectedRoute>
            <VouchersPage />
          </ProtectedRoute>
        } />
        <Route path="/vouchers-admin" element={
          <ProtectedRoute>
            <VouchersAdminPage />
          </ProtectedRoute>
        } />
        <Route path="/sellers" element={
          <ProtectedRoute>
            <SellersPage />
          </ProtectedRoute>
        } />
        <Route path="/seller-sales" element={
          <ProtectedRoute>
            <SellerSalesPage />
          </ProtectedRoute>
        } />
        <Route path="/payment-reports" element={
          <ProtectedRoute>
            <AdminDepositsPage />
          </ProtectedRoute>
        } />
        <Route path="/cortes" element={
          <ProtectedRoute>
            <CortesPage />
          </ProtectedRoute>
        } />
        <Route path="/reportes" element={
          <ProtectedRoute>
            <ReportesPage />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/ticket-designer" element={
          <ProtectedRoute>
            <TicketDesignerSimple />
          </ProtectedRoute>
        } />
        <Route path="/ticket-designer-old" element={
          <ProtectedRoute>
            <TicketDesignerMainPage />
          </ProtectedRoute>
        } />
        <Route path="/vpn-info" element={
          <ProtectedRoute>
            <VPNInfoPage />
          </ProtectedRoute>
        } />
        <Route path="/portal-designer" element={
          <ProtectedRoute>
            <PortalDesignerPage />
          </ProtectedRoute>
        } />
        <Route path="/portal-designer/editor" element={
          <ProtectedRoute>
            <PortalDesignerEditor />
          </ProtectedRoute>
        } />
        <Route path="/portal-designer/editor/:id" element={
          <ProtectedRoute>
            <PortalDesignerEditor />
          </ProtectedRoute>
        } />
        <Route path="/script-generator" element={
          <ProtectedRoute>
            <ScriptsGeneratorPage />
          </ProtectedRoute>
        } />
        <Route path="/scripts/balanceador" element={
          <ProtectedRoute>
            <BalanceadorWizard />
          </ProtectedRoute>
        } />
        <Route path="/scripts/hotspot" element={
          <ProtectedRoute>
            <HotspotWizard />
          </ProtectedRoute>
        } />
        <Route path="/scripts/failover" element={
          <ProtectedRoute>
            <FailoverWizard />
          </ProtectedRoute>
        } />
        <Route path="/scripts/history" element={
          <ProtectedRoute>
            <ScriptsHistory />
          </ProtectedRoute>
        } />
        <Route path="/license-pricing" element={
          <ProtectedRoute>
            <LicensePricingPage />
          </ProtectedRoute>
        } />
        <Route path="/upgrade-plan" element={
          <ProtectedRoute skipLicenseCheck={true}>
            <UpgradePlanPage />
          </ProtectedRoute>
        } />
        <Route path="/tools-purchase" element={
          <ProtectedRoute>
            <ToolsPurchasePage />
          </ProtectedRoute>
        } />
        <Route path="/payment-summary" element={
          <ProtectedRoute>
            <PaymentSummaryPage />
          </ProtectedRoute>
        } />
        <Route path="/pppoe/plans" element={
          <ProtectedRoute>
            <PPPoEPlansPage />
          </ProtectedRoute>
        } />
        <Route path="/pppoe" element={
          <ProtectedRoute>
            <PPPoEClientsPage />
          </ProtectedRoute>
        } />
        <Route path="/pppoe/new" element={
          <ProtectedRoute>
            <NewPPPoEClientPage />
          </ProtectedRoute>
        } />
        <Route path="/pppoe/clients/:clientId" element={
          <ProtectedRoute>
            <PPPoEClientDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/pppoe/reports" element={
          <ProtectedRoute>
            <PPPoEReportsPage />
          </ProtectedRoute>
        } />
        <Route path="/report-payment" element={
          <ProtectedRoute>
            <ReportPaymentPage />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/payment-reports" element={
          <ProtectedRoute>
            <PaymentReportsPage />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/video-tutorials" element={
          <ProtectedRoute>
            <VideoTutorialsManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/whatsapp-notifications" element={
          <ProtectedRoute>
            <WhatsAppNotificationsPage />
          </ProtectedRoute>
        } />
        <Route path="/expenses" element={
          <ProtectedRoute>
            <ExpensesPage />
          </ProtectedRoute>
        } />
        <Route path="/voucher-maintenance" element={
          <ProtectedRoute>
            <VoucherMaintenancePage />
          </ProtectedRoute>
        } />
        <Route path="/maintenance" element={
          <ProtectedRoute>
            <MaintenancePage />
          </ProtectedRoute>
        } />
        <Route path="/pruebauserv7" element={
          <ProtectedRoute>
            <PruebaUserV7 />
          </ProtectedRoute>
        } />
        <Route path="/scripts_del_sistema" element={
          <ProtectedRoute>
            <ScriptsDelSistema />
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
